import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalPlaces: 0,
    newPlaces: 0,
    weeklyPlaces: 0,
    newPlacesChange: '+0%',
    weeklyChange: '+0'
  });

  const [recentPlaces, setRecentPlaces] = useState([]);
  const [topStates, setTopStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const placesResponse = await axios.get(`${API_URL}/places`);
        const places = placesResponse.data;

        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        const newPlacesMonthly = places.filter(place => new Date(place.created_at) > oneMonthAgo);
        
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
        startOfWeek.setHours(0, 0, 0, 0);
        const newPlacesWeekly = places.filter(place => new Date(place.created_at) >= startOfWeek);

        // Group by state for top states
        const stateCount = places.reduce((acc, place) => {
          const state = place.state?.name || place.state || 'Unknown';
          acc[state] = (acc[state] || 0) + 1;
          return acc;
        }, {});
        
        const sortedStates = Object.entries(stateCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name, count]) => ({ name, count, percentage: Math.round((count / places.length) * 100) }));

        setTopStates(sortedStates);

        // Generate activity data for chart (last 7 days)
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dayPlaces = places.filter(p => {
            const pDate = new Date(p.created_at);
            return pDate.toDateString() === date.toDateString();
          }).length;
          last7Days.push({
            day: date.toLocaleDateString('en-US', { weekday: 'short' }),
            count: dayPlaces
          });
        }
        setActivityData(last7Days);

        setStats({
          totalPlaces: places.length,
          newPlaces: newPlacesMonthly.length,
          weeklyPlaces: newPlacesWeekly.length,
          newPlacesChange: `+${Math.round((newPlacesMonthly.length / (places.length || 1)) * 100)}%`,
          weeklyChange: `+${newPlacesWeekly.length}`
        });

        const sortedPlaces = [...places]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);

        setRecentPlaces(sortedPlaces.map(place => ({
          id: place.id,
          name: place.name,
          city: place.city,
          state: place.state?.name || place.state,
          date: new Date(place.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          image: place.images?.[0]?.image_url || place.images?.[0] || null
        })));

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Generate sparkline path
  const generateSparkline = (data, height = 40) => {
    if (!data || data.length === 0) return '';
    const max = Math.max(...data, 1);
    const width = 100;
    const points = data.map((val, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (val / max) * height * 0.8;
      return `${x},${y}`;
    });
    return `M ${points.join(' L ')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  const maxActivity = Math.max(...activityData.map(d => d.count), 1);

  return (
    <div className="space-y-6 pb-8">
      
      {/* ===== HEADER ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm text-white/40 mb-1">
            Welcome back, <span className="text-purple-400">{user?.name?.split(' ')[0] || 'Admin'}</span>
          </p>
          <h1 className="text-3xl font-extralight tracking-tight text-white">
            Dashboard<span className="text-purple-400 font-bold">.</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/analytics"
            className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.03] border border-white/10 text-white/70 hover:text-white hover:border-white/20 rounded-xl text-sm font-medium transition-all"
          >
            <span className="material-symbols-outlined text-lg">trending_up</span>
            Analytics
          </Link>
          <Link
            to="/places/new"
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-purple-500/25"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Add Place
          </Link>
        </div>
      </div>

      {/* ===== KPI STATS GRID ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        
        {/* Total Places */}
        <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white/40 text-sm">Total Places</p>
              <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">{stats.newPlacesChange}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="text-4xl font-light text-white">{stats.totalPlaces}</h2>
              <span className="text-white/30 text-sm">destinations</span>
            </div>
            <svg className="w-full h-10" viewBox="0 0 100 40">
              <path 
                d={generateSparkline([12, 18, 15, 22, 28, 25, stats.totalPlaces])}
                fill="none"
                stroke="url(#purpleGradient)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* New This Month */}
        <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-indigo-500/30 transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white/40 text-sm">New This Month</p>
              <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">+{stats.newPlaces}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="text-4xl font-light text-white">{stats.newPlaces}</h2>
              <span className="text-white/30 text-sm">added</span>
            </div>
            <svg className="w-full h-10" viewBox="0 0 100 40">
              <path 
                d={generateSparkline([5, 8, 12, 10, 15, 18, stats.newPlaces])}
                fill="none"
                stroke="url(#indigoGradient)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="indigoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* This Week */}
        <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:border-cyan-500/30 transition-all duration-300">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white/40 text-sm">This Week</p>
              <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded-full">{stats.weeklyChange}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="text-4xl font-light text-white">{stats.weeklyPlaces}</h2>
              <span className="text-white/30 text-sm">places</span>
            </div>
            <svg className="w-full h-10" viewBox="0 0 100 40">
              <path 
                d={generateSparkline([2, 4, 3, 5, 4, 6, stats.weeklyPlaces])}
                fill="none"
                stroke="url(#cyanGradient)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="cyanGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

      </div>

      {/* ===== MIDDLE SECTION ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Activity Chart */}
        <div className="lg:col-span-2 bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-white">Activity Overview</h3>
              <p className="text-sm text-white/40">Places added this week</p>
            </div>
            <div className="flex gap-2">
              {['1W', '1M', '1Y'].map((period, i) => (
                <button 
                  key={period}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    i === 0 
                      ? 'bg-purple-500/20 text-purple-400' 
                      : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          {/* Bar Chart */}
          <div className="h-48 flex items-end justify-between gap-2">
            {activityData.map((day) => (
              <div key={day.day} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full relative">
                  <div 
                    className="w-full bg-gradient-to-t from-purple-600 to-indigo-500 rounded-t-lg transition-all duration-500 hover:from-purple-500 hover:to-indigo-400"
                    style={{ 
                      height: `${Math.max((day.count / maxActivity) * 150, 8)}px`,
                      opacity: 0.3 + (day.count / maxActivity) * 0.7
                    }}
                  />
                  {day.count > 0 && (
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-white/60">
                      {day.count}
                    </span>
                  )}
                </div>
                <span className="text-xs text-white/40">{day.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top States */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6">
          <h3 className="text-lg font-medium text-white mb-6">Top States</h3>
          <div className="space-y-4">
            {topStates.length > 0 ? topStates.map((state, index) => (
              <div key={state.name} className="group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-lg bg-purple-500/20 flex items-center justify-center text-xs text-purple-400 font-medium">
                      {index + 1}
                    </span>
                    <span className="text-sm text-white/80">{state.name}</span>
                  </div>
                  <span className="text-sm text-purple-400 font-medium">{state.count}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-600 to-indigo-500 rounded-full transition-all duration-700"
                    style={{ width: `${state.percentage}%` }}
                  />
                </div>
              </div>
            )) : (
              <p className="text-sm text-white/40 text-center py-4">No data available</p>
            )}
          </div>
        </div>
      </div>

      {/* ===== BOTTOM SECTION ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Recent Places */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-white/5">
            <h3 className="text-lg font-medium text-white">Recent Places</h3>
            <Link 
              to="/places" 
              className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
              View all
              <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {recentPlaces.length > 0 ? recentPlaces.map((place) => (
              <Link
                key={place.id}
                to={`/places/edit/${place.id}`}
                className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/5 flex-shrink-0">
                  {place.image ? (
                    <img src={place.image} alt={place.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-white/20">image</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate group-hover:text-purple-400 transition-colors">{place.name}</p>
                  <p className="text-xs text-white/40">{place.city}, {place.state}</p>
                </div>
                <span className="text-xs text-white/30">{place.date}</span>
              </Link>
            )) : (
              <div className="p-8 text-center text-white/40">
                No places added yet
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6">
          <h3 className="text-lg font-medium text-white mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: 'add_location', label: 'Add Place', path: '/places/new', color: 'purple' },
              { icon: 'auto_stories', label: 'Journals', path: '/journals', color: 'cyan' },
              { icon: 'chat_bubble', label: 'Feedback', path: '/feedback', color: 'amber' },
            ].map((action) => (
              <Link
                key={action.label}
                to={action.path}
                className={`group flex flex-col items-center justify-center p-6 rounded-xl bg-white/[0.02] border border-white/5 hover:border-${action.color}-500/30 hover:bg-${action.color}-500/5 transition-all duration-300`}
              >
                <div className={`w-12 h-12 rounded-xl bg-${action.color}-500/10 flex items-center justify-center mb-3 group-hover:bg-${action.color}-500/20 transition-colors`}>
                  <span className={`material-symbols-outlined text-2xl text-${action.color}-400`}>
                    {action.icon}
                  </span>
                </div>
                <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">{action.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
